import React, { useImperativeHandle, useRef } from 'react';

import { Camera, Image as ImageIcon, Plus } from 'lucide-react';

import { cn } from './utils';


/**
 * File-upload primitive (.pen "Sec-File upload", n6rvQ).
 *
 * Encapsulates a single hidden `<input type="file">` so feature code never holds
 * the raw element. The picker is opened either by clicking the default dropzone
 * or via the `open()` callback handed to a render-prop child (so a page can wire
 * its own trigger — a Button, an add-tile — while the input stays inside here).
 *
 * The component is intentionally unopinionated about server contract: it surfaces
 * the picked files (`onSelect(files: File[])`) and/or the raw change event
 * (`onChange(event)`), then clears the input value so re-picking the same file
 * fires again. Consumers keep their existing FormData / mutation / primary-photo
 * logic unchanged.
 *
 * Composable visual sub-parts (`FileUpload.Dropzone`, `FileUpload.ThumbGrid`,
 * `FileUpload.Thumb`, `FileUpload.AddTile`) render the .pen dropzone + thumb grid
 * so single-image (avatar / logo) and multi-image (part photos) sites share one
 * look without forcing one behavior.
 */
const FileUpload = React.forwardRef((props, ref) => {
  const {
    accept = 'image/*',
    multiple = false,
    disabled = false,
    capture,
    onSelect,
    onChange,
    className,
    inputClassName,
    children,
    ...restProps
  } = props;

  const inputRef = useRef(null);
  // Expose the real <input> to callers that still need ref access (focus, etc.)
  // while keeping the element itself encapsulated in here.
  useImperativeHandle(ref, () => inputRef.current, []);

  const open = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleChange = (event) => {
    onChange?.(event);
    if (onSelect) {
      onSelect(Array.from(event.target.files || []));
    }
    // Reset so selecting the same file again still triggers a change.
    event.target.value = '';
  };

  return (
    <div className={cn('contents', className)} {...restProps}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture={capture}
        disabled={disabled}
        multiple={multiple}
        className={cn('hidden', inputClassName)}
        onChange={handleChange}
      />

      {typeof children === 'function'
        ? children({ open, disabled })
        : children ?? <FileUpload.Dropzone disabled={disabled} onClick={open} />}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';


/** The .pen dropzone: rounded surface-2 card, camera icon + primary/sub text,
 * click to open the picker. `as={false}` renders a non-interactive shell. */
function Dropzone({
  title = 'Drag photos or tap to capture',
  subtitle,
  icon: Icon = Camera,
  disabled = false,
  className,
  onClick,
  ...restProps
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'flex h-[110px] w-[280px] max-w-full flex-col items-center justify-center gap-2 rounded-lg',
        'border border-hairline-strong bg-surface-2 text-center outline-none',
        'focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      onClick={onClick}
      {...restProps}
    >
      <Icon className="size-[26px] text-primary" />
      <span className="text-[12.5px] font-medium text-ink">{title}</span>

      {subtitle
        ? <span className="text-[11px] text-ink-subtle">{subtitle}</span>
        : null}
    </button>
  );
}

Dropzone.displayName = 'FileUpload.Dropzone';


/** Flex-wrap container for the .pen 64px thumb grid. */
function ThumbGrid({ className, children, ...restProps }) {
  return (
    <div className={cn('flex flex-wrap gap-2.5', className)} {...restProps}>
      {children}
    </div>
  );
}

ThumbGrid.displayName = 'FileUpload.ThumbGrid';


/** A 64px thumb. `primary` swaps to the 2px indigo border; otherwise hairline. */
function Thumb({
  primary = false,
  src,
  alt = '',
  className,
  children,
  ...restProps
}) {
  return (
    <span
      className={cn(
        'relative grid size-16 place-items-center overflow-hidden rounded-md bg-surface-1',
        primary ? 'border-2 border-primary' : 'border border-hairline',
        className,
      )}
      {...restProps}
    >
      {src
        ? <img alt={alt} src={src} className="size-full object-cover" />
        : <ImageIcon className="size-5 text-ink-subtle" />}

      {children}
    </span>
  );
}

Thumb.displayName = 'FileUpload.Thumb';


/** The .pen add-tile: surface-2 + hairline-strong + plus icon, opens the picker. */
function AddTile({
  icon: Icon = Plus,
  disabled = false,
  className,
  onClick,
  children,
  ...restProps
}) {
  return (
    <button
      type="button"
      aria-label="Add"
      disabled={disabled}
      className={cn(
        'grid size-16 place-items-center rounded-md border border-hairline-strong bg-surface-2',
        'text-ink-subtle outline-none focus-visible:ring-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      onClick={onClick}
      {...restProps}
    >
      {children ?? <Icon className="size-5" />}
    </button>
  );
}

AddTile.displayName = 'FileUpload.AddTile';


FileUpload.Dropzone = Dropzone;
FileUpload.ThumbGrid = ThumbGrid;
FileUpload.Thumb = Thumb;
FileUpload.AddTile = AddTile;

export { FileUpload };
export default FileUpload;
